import { ActionIcon, Box, Group, Icon, ScrollArea } from '@inflearn/ds-react-next';
import { faAngleLeft, faAngleRight } from '@inflearn/pro-solid-svg-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UpcomingEventResponse } from '../../../../../../resources/@types/courseSchema';
import { UpcomingEventCard } from './UpcomingEventCard';

interface UpcomingEventListProps {
  events: UpcomingEventResponse[];
}

export function UpcomingEventList({ events }: UpcomingEventListProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!scrollAreaRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollAreaRef.current;
    const visibleEndPosition = scrollLeft + clientWidth; // 전체 scrollWidth 에서 현재 보이는 영역의 끝
    const endPosition = scrollWidth - 1; // 전체 scrollWidth 의 끝 위치

    const isScrolledFromStart = scrollLeft > 0;
    const hasMoreContentOnRight = visibleEndPosition < endPosition; // visibleEndPosition 이 endPosition 보다 작으면 더 스크롤할 수 있음

    setCanScrollLeft(isScrolledFromStart);
    setCanScrollRight(hasMoreContentOnRight);
  }, []);

  useEffect(() => {
    const viewport = scrollAreaRef.current;
    if (!viewport) {
      return;
    }

    updateScrollState();
    viewport.addEventListener('scroll', updateScrollState);

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  const handleScrollLeft = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (events.length === 0) {
    return null;
  }

  return (
    <Box pos="relative">
      <ScrollArea type="never" viewportRef={scrollAreaRef}>
        <Group gap="8px" wrap="nowrap" pr={48 + 16}>
          {events.map((event) => (
            <UpcomingEventCard key={`${event.courseId}-${event.unitId}`} event={event} />
          ))}
        </Group>
      </ScrollArea>
      {canScrollLeft && (
        <Box
          pos="absolute"
          left={0}
          top="50%"
          pr={16}
          py={8}
          css={{
            transform: 'translateY(-50%)',
            background: 'linear-gradient(to left, transparent 0%, white 20%)',
          }}
        >
          <ActionIcon
            variant="default"
            color="gray"
            size="n-md"
            radius="xl"
            onClick={handleScrollLeft}
          >
            <Icon icon={faAngleLeft} size="md" color="gray.9" />
          </ActionIcon>
        </Box>
      )}
      {canScrollRight && (
        <Box
          pos="absolute"
          right={0}
          top="50%"
          pl={16}
          py={8}
          css={{
            transform: 'translateY(-50%)',
            background: 'linear-gradient(to right, transparent 0%, white 20%)',
          }}
        >
          <ActionIcon
            variant="default"
            color="gray"
            size="n-md"
            radius="xl"
            onClick={handleScrollRight}
          >
            <Icon icon={faAngleRight} size="md" color="gray.9" />
          </ActionIcon>
        </Box>
      )}
    </Box>
  );
}
